// Pure helpers for the New Mission canvas. No React, no MapLibre — all unit-testable.
//
// The canvas replaces the create dialog: an operator draws WHERE on a map, picks WHO from the
// rail, optionally sets WHEN, and commits. Everything here is the part of that flow that can be
// reasoned about without a browser.

import type { Drone } from '@/types/fleet';
import { haversineDistanceM } from '@/lib/mission-planning';
import {
  scheduleReadiness,
  utcInputValueToIso,
  type ScheduleReadiness,
} from './missionDetailShared';
import { aMissionType } from '@/lib/copy';
import { orbitAircraftRefusal, type LaunchAircraft } from '@/lib/mission-launchable';
import {
  UNFLYABLE_MISSION_TYPES,
  waypointCountRangeForType,
  type MissionType,
} from '@/lib/mission-type';

/**
 * A point the operator has placed on the canvas. Not yet persisted — there is no mission id.
 *
 * `id` is client-side only and exists so a map marker can be bound to a waypoint rather than to
 * its array index. Index-bound markers write to the wrong slot the moment a point is removed from
 * the middle of the route: the markers still on screen keep their old indices, so dragging the one
 * labelled "3" can move what is now waypoint 4.
 */
export interface DraftWaypoint {
  id: string;
  lat: number;
  lng: number;
  /**
   * This point's OWN altitude in metres AGL, or `undefined` to follow the mission altitude.
   *
   * The absence of a number is the whole design. The mission altitude is the DEFAULT every point
   * inherits, so raising it has to raise every point the operator never touched — and must NOT
   * touch one they did. Eagerly stamping the mission altitude onto each point on creation would
   * make those two states indistinguishable the moment they happen to be equal, and the only way
   * back would be to guess. `undefined` means "follows the mission"; a number means "the operator
   * said this one", and nothing but the operator rewrites it.
   *
   * Consistent with the altitude box's own rule (`parseAltitudeInput`): on a military product a
   * value the operator typed is reported on, never quietly rewritten.
   */
  altitudeM?: number;
}

let waypointSeq = 0;

/**
 * A draft point with a fresh client-side id. Monotonic, so ids are never reused within a session.
 *
 * Deliberately no altitude: a point placed on the map inherits the mission altitude until the
 * operator says otherwise.
 */
export function makeDraftWaypoint(lat: number, lng: number): DraftWaypoint {
  waypointSeq += 1;
  return { id: `wp-${waypointSeq}`, lat, lng };
}

/**
 * The altitude a point would actually be flown at: its own when it has one, the mission's
 * otherwise.
 *
 * One function so the payload, the keep-in validation, the dirty check and the row all resolve
 * "inherits" the same way. Two of those disagreeing is how a route gets validated at one altitude
 * and saved at another.
 */
export function effectiveWaypointAltitude(wp: DraftWaypoint, missionAltitudeM: number): number {
  return wp.altitudeM !== undefined && Number.isFinite(wp.altitudeM)
    ? wp.altitudeM
    : clampCanvasAltitude(missionAltitudeM);
}

/**
 * A stored route in the shape the readiness rail reads.
 *
 * The rail ranks the fleet against the route actually drawn, and the mission page draws the same
 * routes the canvas does — just ones that already exist. It only ever reads `lat`/`lng` for the
 * verdict and `id` as a React key, so this is the whole adapter.
 *
 * Lives here rather than in `missionDetailShared` because that module must not import this one:
 * the dependency already runs the other way and reversing it would be a cycle.
 *
 * A point the operator has just clicked onto the map has no server id yet, so the index stands in.
 * That is only a key, never an identity — nothing downstream matches waypoints on it.
 *
 * A STORED altitude always comes back as an explicit per-point value, never as "inherits". The
 * server holds one number per waypoint row and has no notion of inheritance, so that number IS
 * what the aircraft would fly — and re-deriving it from the mission's default on load is exactly
 * the bug this closes: `CopMissionEdit` used to stamp `mission.default_altitude` over every stored
 * waypoint on every save, which was invisible only while all of them were equal.
 *
 * Kept verbatim rather than clamped to the canvas range: the value is one the API itself accepted
 * (`waypointItemSchema` allows 10–500, wider than the canvas's 10–400 intersection), so clamping
 * here would silently lower an altitude on a point the operator never touched.
 */
export function toDraftWaypoints(
  waypoints: ReadonlyArray<{ id?: string; latitude: number; longitude: number; altitude?: number | null }>,
): DraftWaypoint[] {
  return waypoints.map((wp, i) => ({
    id: wp.id ?? `unsaved-${i}`,
    lat: wp.latitude,
    lng: wp.longitude,
    // Only a real number is an altitude. A null/absent column value means the row never carried
    // one, which is "follow the mission", not "fly at zero".
    ...(typeof wp.altitude === 'number' && Number.isFinite(wp.altitude) ? { altitudeM: wp.altitude } : {}),
  }));
}

/**
 * Altitude bounds for a drawn route.
 *
 * Deliberately the INTERSECTION of the two schemas a canvas route has to satisfy:
 * `createSchema.default_altitude` on the mission (10–400) and `waypointSchema.altitude` on the
 * waypoints (10–500). Using the wider waypoint range would let an operator draw a route at 450 m
 * that the mission create call then rejects. Until the two are reconciled server-side, the canvas
 * stays inside both.
 */
export const CANVAS_ALT_MIN_M = 10;
export const CANVAS_ALT_MAX_M = 400;
export const CANVAS_ALT_DEFAULT_M = 50;

/** `missions.name` is `z.string().min(1).max(100)`; a derived name must not overrun it. */
export const MISSION_NAME_MAX = 100;

/** `waypointSchema` is `.min(2).max(100)` — a longer route is rejected wholesale, not truncated. */
export const MAX_ROUTE_WAYPOINTS = 100;

export function clampCanvasAltitude(meters: number): number {
  if (!Number.isFinite(meters)) return CANVAS_ALT_DEFAULT_M;
  return Math.min(CANVAS_ALT_MAX_M, Math.max(CANVAS_ALT_MIN_M, Math.round(meters)));
}

export interface AltitudeEntry {
  meters: number;
  /** Set when the typed value was not usable as-is, so the change can be shown, never silent. */
  note: string | null;
}

/**
 * Read the altitude box.
 *
 * A blank field must fall back to the default, NOT to the minimum: `Number('')` is `0`, and
 * clamping 0 lands on 10 m. An operator who selects the "50" and deletes it intending to retype
 * would otherwise end up flying the whole route at 10 m AGL without being told.
 *
 * Clamping is reported rather than applied silently — on a military product, quietly rewriting an
 * altitude the operator typed is the wrong default.
 */
export function parseAltitudeInput(raw: string): AltitudeEntry {
  const trimmed = raw.trim();
  if (trimmed === '') return { meters: CANVAS_ALT_DEFAULT_M, note: `Empty — using ${CANVAS_ALT_DEFAULT_M} m` };

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return { meters: CANVAS_ALT_DEFAULT_M, note: `Not a number — using ${CANVAS_ALT_DEFAULT_M} m` };

  const clamped = clampCanvasAltitude(parsed);
  if (clamped !== Math.round(parsed)) {
    return { meters: clamped, note: `Outside ${CANVAS_ALT_MIN_M}–${CANVAS_ALT_MAX_M} m — clamped to ${clamped} m` };
  }
  return { meters: clamped, note: null };
}

export interface WaypointAltitudeEntry {
  /** The override to store, or null for "follow the mission altitude". */
  meters: number | null;
  /** Set when the typed value was not usable as-is. Same rule: shown, never silently applied. */
  note: string | null;
}

/**
 * Read ONE waypoint's altitude box.
 *
 * Differs from `parseAltitudeInput` in exactly one place, and deliberately: an EMPTY box here is
 * not an error to fall back from, it is the way back to the mission altitude. The mission field
 * has no such state — something has to be the default, so blank there means "use 50" — but a
 * waypoint always has a default behind it, so clearing the box returns the point to it. That is
 * also the only affordance for un-doing an override, which is why it must not be a note.
 *
 * Everything else matches: same bounds, same clamp, and the clamp is REPORTED rather than applied
 * silently.
 */
export function parseWaypointAltitudeInput(raw: string): WaypointAltitudeEntry {
  const trimmed = raw.trim();
  if (trimmed === '') return { meters: null, note: null };

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return { meters: null, note: 'Not a number — following the mission altitude' };

  const clamped = clampCanvasAltitude(parsed);
  if (clamped !== Math.round(parsed)) {
    return { meters: clamped, note: `Outside ${CANVAS_ALT_MIN_M}–${CANVAS_ALT_MAX_M} m — clamped to ${clamped} m` };
  }
  return { meters: clamped, note: null };
}

/** 24h UTC stamp, aviation style: `0912Z`. */
function utcStamp(now: Date): string {
  const hh = String(now.getUTCHours()).padStart(2, '0');
  const mm = String(now.getUTCMinutes()).padStart(2, '0');
  return `${hh}${mm}Z`;
}

/**
 * The name the canvas proposes, so creating a mission needs no typing at all.
 *
 * Name is the ONLY field the API actually requires, so deriving it is what removes the last
 * mandatory keystroke. It is a starting point, not a decision — the operator can overwrite it,
 * and once they do the derived name is never reapplied (see `_cop/CopMissionCompose`).
 *
 * Deterministic on purpose: `now` is passed in rather than read, so this is testable and two
 * calls in the same minute agree.
 */
export function deriveMissionName(
  siteName: string | null,
  waypoints: DraftWaypoint[],
  now: Date,
): string {
  const stamp = utcStamp(now);
  const where = siteName?.trim()
    ? siteName.trim().toUpperCase()
    : waypoints.length > 0
      ? `${waypoints[0].lat.toFixed(4)} ${waypoints[0].lng.toFixed(4)}`
      : null;

  const name = where ? `${where} · ${stamp}` : `MISSION · ${stamp}`;
  return name.length > MISSION_NAME_MAX ? `${name.slice(0, MISSION_NAME_MAX - 1)}…` : name;
}

/**
 * Which instant the derived name should stamp.
 *
 * The canvas opens before the operator knows when the sortie flies, so the stamp starts at "now".
 * The moment a departure is set that is the wrong answer: a mission stamped `0912Z` that departs
 * at `1400Z` is a name that lies on the board, and the board is sorted and read by that name. A
 * valid departure therefore wins; a blank or half-typed one (the box emits junk mid-keystroke)
 * keeps the open time rather than blanking the name.
 *
 * `openedAt` rather than `new Date()` so this is deterministic and the caller can derive during
 * render without a second pass.
 */
export function nameStampAt(departureInput: string, openedAt: Date): Date {
  const iso = utcInputValueToIso(departureInput);
  if (!iso) return openedAt;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms) : openedAt;
}

export interface CanvasGates {
  /** The three `draft → scheduled` checks, evaluated against what THIS submit will persist. */
  readiness: ScheduleReadiness;
  /** Whether the mission can be created at all. Only the name gates creation. */
  canCreate: boolean;
  /** Why creation is blocked, or null. */
  createBlocker: string | null;
  /** True when the drawn route will actually be saved — `waypointSchema` accepts 2 to 100 points. */
  routeWillPersist: boolean;
  /**
   * Why a drawn route will NOT be saved, or null. Covers both ends: one point is below
   * `waypointSchema.min(2)`, and over 100 is above its `.max(100)` — that one rejects the whole
   * array, so a 110-point route persists nothing at all.
   */
  routeWarning: string | null;
}

/**
 * What the commit strip renders.
 *
 * Note the difference from the mission-detail use of `scheduleReadiness`, whose doc warns that
 * `waypointCount` must be the server's count and never a route still being drawn. That warning is
 * about a mission that already exists, where the drawn route and the stored route can diverge.
 * Here they cannot: the same commit writes both, so the draft count IS what the server will hold
 * a moment later. The strip forecasts the mission being created, it does not describe one that
 * exists.
 *
 * These three checks never block creation — they are advisory here, because a mission is created
 * as a `draft` and the API only enforces them at the schedule transition. Blocking creation on
 * them would reintroduce exactly the premature gate this redesign removes.
 */
export function canvasGates(
  name: string,
  droneId: string | null,
  pilotId: string | null,
  waypoints: DraftWaypoint[],
  /**
   * 022. Trailing and defaulted so every existing caller keeps the behaviour it had: `route`
   * is the column's own default and what a mission was before types existed, so the 2..100
   * bound below is unchanged for it.
   */
  missionType: MissionType = 'route',
): CanvasGates {
  const bounds = waypointCountRangeForType(missionType);
  const routeWillPersist =
    waypoints.length >= bounds.min && waypoints.length <= bounds.max;

  // Feed the readiness checklist the count that will actually be STORED. Passing the raw draft
  // length would render a green "Route has 110 waypoints" for a route the API rejects wholesale.
  const readiness = scheduleReadiness(
    { assigned_drone_id: droneId, assigned_pilot_id: pilotId },
    routeWillPersist ? waypoints.length : 0,
    missionType,
  );

  const trimmed = name.trim();
  const createBlocker = !trimmed
    ? 'Name the mission'
    : trimmed.length > MISSION_NAME_MAX
      ? `Name is too long (${trimmed.length}/${MISSION_NAME_MAX})`
      : null;

  return {
    readiness,
    canCreate: createBlocker === null,
    createBlocker,
    routeWillPersist,
    routeWarning: routeWarningFor(missionType, waypoints.length, bounds),
  };
}

/**
 * The route warning, in the type's own terms.
 *
 * A single-point type has the opposite problem to a route: too MANY points, never too few, and
 * telling an operator drawing a GO TO POINT to "add one more" would walk them into a save the
 * API refuses.
 */
function routeWarningFor(
  missionType: MissionType,
  count: number,
  bounds: { min: number; max: number },
): string | null {
  if (count === 0) return null;
  if (count < bounds.min) {
    return `A route needs at least ${bounds.min} points — add one more or it will not be saved`;
  }
  if (count > bounds.max) {
    return bounds.max === 1
      ? `${aMissionType(missionType, { capitalised: true })} takes one point, so remove ${count - 1} or none will be saved`
      : `${count} points exceeds the ${bounds.max} limit — remove ${count - bounds.max} or none will be saved`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// The NOW lane — "launch this mission right now" from the canvas.
//
// Two lanes leave the WHEN control. SCHEDULE writes a plan and stops; NOW is a
// real takeoff: the mission is created, its route saved, and the aircraft
// commanded, in one action, skipping draft → scheduled entirely.
//
// The preconditions below are therefore not the same as the scheduling
// checklist. Scheduling is paperwork and its unmet checks are advisory; a
// launch needs an actual airframe and an actual route, and neither can be
// resolved later by someone else showing up.
// ---------------------------------------------------------------------------

/** Which lane the WHEN control is in. `later` is the default — plan, don't fly. */
export type WhenMode = 'later' | 'now';

export interface NowLaunchGate {
  /** True only when nothing is blocking. */
  canLaunch: boolean;
  /**
   * Every unmet precondition, in the order the operator should fix them. Rendered in
   * full rather than used to grey a button out: a disabled LAUNCH with no reason beside
   * it is the failure this exists to prevent.
   */
  blockers: string[];
  /**
   * A caution to show INSTEAD of the green "ready" line, when nothing blocks the launch but the
   * readiness verdict is not a confirmed GO for the route as drawn. Null when it genuinely is.
   *
   * Exists because "we do not block on this" and "we call this ready" are different claims, and
   * collapsing them printed a green **Aircraft and route ready** over an aircraft whose verdict
   * was UNKNOWN — which is the state on every first entry into the lane, on every readiness
   * failure, and for the debounce after every waypoint drag. That is the same contradiction the
   * NO-GO blocker was written to close, for the case that happens far more often.
   */
  advisory: string | null;
}

/**
 * What the NOW lane needs before it commands anything.
 *
 * `routeWillPersist` is reused rather than recomputed: the route the aircraft flies is
 * the route the server stored, so "a route the API will accept" and "a route worth
 * launching" have to be the same question. A 1-point or 101-point route is not saved at
 * all, and launching a mission whose route silently did not persist would fly nothing.
 *
 * The name blocker is folded in because a NOW launch still creates the mission first —
 * if it cannot be created it certainly cannot be launched, and surfacing that only after
 * the operator has committed to a takeoff is the wrong moment to find out.
 *
 * `selectedVerdict` is the rail's verdict for the chosen aircraft, and a NO-GO one is a
 * BLOCKER here. It was not consulted at all before, so the strip rendered a green
 * "Aircraft and route ready" next to an enabled LAUNCH while the rail two inches above showed
 * that same airframe red with "Battery 12% — below the 25% launch minimum". The operator was
 * told the opposite of the truth at the exact moment they committed to a takeoff.
 *
 * An `unknown` verdict is deliberately NOT a blocker: it means the readiness request has not
 * answered (or failed), and refusing to launch because a poll is in flight would ground the
 * fleet on a network blip. The server re-runs the real gate and can still refuse — which is
 * the division of labour this whole surface is built on.
 */
export function nowLaunchGate(
  droneId: string | null,
  waypoints: DraftWaypoint[],
  routeWillPersist: boolean,
  createBlocker: string | null,
  selectedVerdict: { verdict: RailVerdict; callsign: string; reasons: string[] } | null = null,
  /**
   * The verdict does not answer for the route as it stands — the readiness request is in flight,
   * debouncing behind a route change, or failed. A GO from before the operator dragged a
   * waypoint into a keep-out area is not a GO for what they are about to fly.
   */
  verdictStale = false,
  /** 022. Trailing and defaulted, for the reason `canvasGates` documents. */
  missionType: MissionType = 'route',
  /**
   * The chosen aircraft, for the refusals that depend on it rather than on the mission.
   *
   * Today that is the ORBIT pair: a dock-stationed airframe has no automatic way home from a
   * circle, and a non-companion one has no circular flight path at all. Null means the caller
   * cannot see the aircraft, and then nothing is predicted - the server still answers.
   */
  aircraft: LaunchAircraft | null = null,
  /**
   * A blocker the surface computed for the mission's own parameters - the orbit radius box.
   *
   * Passed in rather than recomputed because the parse lives with the input. It belongs on THIS
   * strip: the panel used to show the radius fault as an amber line near the top of a scrolling
   * form while the commit strip at the bottom - the thing an operator reads before arming -
   * printed a green "Aircraft and route ready" over an enabled LAUNCH.
   */
  paramBlocker: string | null = null,
): NowLaunchGate {
  const blockers: string[] = [];

  if (createBlocker) blockers.push(createBlocker);
  if (paramBlocker) blockers.push(paramBlocker);

  // The aircraft's own refusal for this type, in the operator's words and BEFORE the arm.
  const aircraftIssue = orbitAircraftRefusal(missionType, aircraft);
  if (aircraftIssue) blockers.push(aircraftIssue);

  // 022. Before the aircraft and before the route, because neither can change the answer —
  // exactly the order the launch service refuses in. Withheld here rather than offered and
  // refused: the operator should never be able to arm a launch the server will reject.
  const flightIssue = UNFLYABLE_MISSION_TYPES[missionType];
  if (flightIssue) blockers.push(flightIssue);

  if (!droneId) blockers.push('Pick an aircraft — a launch needs one');

  if (!routeWillPersist) {
    const bounds = waypointCountRangeForType(missionType);
    blockers.push(
      waypoints.length === 0
        ? bounds.min === 1
          // Same rule as `scheduleReadiness`: "the point" only when the type takes exactly one.
          // HOVER and OUT & BACK have min 1 and a max above it, so they get the plural form.
          ? bounds.max === 1
            ? `Place the point. ${aMissionType(missionType, { capitalised: true })} launch needs one.`
            : `Place at least one waypoint. ${aMissionType(missionType, { capitalised: true })} launch needs one.`
          : `Draw a route — a launch needs at least ${bounds.min} waypoints`
        : waypoints.length < bounds.min
          ? `A route needs at least ${bounds.min} points — add one more`
          : bounds.max === 1
            ? `${aMissionType(missionType, { capitalised: true })} takes one point, so remove ${waypoints.length - 1}`
            : `${waypoints.length} points exceeds the ${bounds.max} limit — remove ${waypoints.length - bounds.max}`,
    );
  }

  if (selectedVerdict?.verdict === 'no_go') {
    // The reasons come straight from the server, so the strip says the same words the rail row
    // does rather than paraphrasing them into a second vocabulary.
    blockers.push(
      selectedVerdict.reasons.length > 0
        ? `${selectedVerdict.callsign} is NO-GO — ${selectedVerdict.reasons[0]}`
        : `${selectedVerdict.callsign} is NO-GO — the server will refuse this launch`,
    );
  }

  // Not a blocker — the server is the only thing that can refuse, and grounding the fleet
  // because a 20s poll is in flight would be worse than launching on a stale GO. But it is not
  // "ready" either, and the strip must not say so.
  let advisory: string | null = null;
  if (blockers.length === 0) {
    const callsign = selectedVerdict?.callsign ?? 'This aircraft';
    // "Never answered" is checked BEFORE "answered but out of date": with no verdict at all there
    // is nothing to re-check, and calling that state "re-checking" would imply we once had one.
    if (selectedVerdict === null || selectedVerdict.verdict === 'unknown') {
      advisory = `Route ready — ${callsign} readiness not evaluated. The server re-checks at launch.`;
    } else if (verdictStale) {
      advisory = `Route ready — re-checking ${callsign} against it. The server re-checks at launch.`;
    }
  }

  return { canLaunch: blockers.length === 0, blockers, advisory };
}

/**
 * Waypoint rows for `PUT /api/missions/:id/waypoints`, in the order they were placed.
 *
 * `altitudeM` is the mission altitude — the DEFAULT, not an override. A point that carries its own
 * altitude keeps it; every other point is stamped with the mission's, which is what makes the
 * altitude field on the compose form mean what the operator was promised: "the ALT I set on
 * create is the ALT attached to every WP".
 *
 * The mission fallback is clamped to the canvas range; an explicit per-point altitude is NOT. It
 * has already been through `parseWaypointAltitudeInput` (clamped, with the clamp reported) if the
 * operator typed it, or it came back from the server, which accepts a wider range than the canvas
 * offers — re-clamping would quietly lower a stored altitude on a point nobody edited. Only a
 * value that is not a number at all falls back, because that is not an altitude.
 */
export function draftToWaypointPayload(waypoints: DraftWaypoint[], altitudeM: number) {
  const missionAltitude = clampCanvasAltitude(altitudeM);
  return waypoints.map((wp) => ({
    latitude: wp.lat,
    longitude: wp.lng,
    altitude: wp.altitudeM !== undefined && Number.isFinite(wp.altitudeM) ? wp.altitudeM : missionAltitude,
    action: 'fly_through' as const,
  }));
}

// ---------------------------------------------------------------------------
// Readiness rail — the fleet picker that ranks aircraft by who can fly THIS route.
//
// Everything below is the part of the rail that can be reasoned about without a browser: the wire
// contract of `GET /api/drones/readiness`, the merge of that verdict onto the roster the canvas
// already holds, and the formatting each row prints. The component is a thin renderer over these.
// ---------------------------------------------------------------------------

/** Mirrors `ReadinessCheckStatus` in `src/services/aircraft-readiness.ts`. */
export type ReadinessCheckStatus = 'go' | 'no_go' | 'skipped';

export interface ReadinessCheck {
  status: ReadinessCheckStatus;
  /** Operator-facing explanation; null when the check passed cleanly. */
  detail: string | null;
}

/** One aircraft's verdict, exactly as `GET /api/drones/readiness` returns it. */
export interface DroneReadiness {
  drone_id: string;
  name: string;
  callsign: string | null;
  status: string;
  is_online: boolean;
  ready: boolean;
  /** One message per failed check, in check order. Empty ⇒ GO. */
  reasons: string[];
  battery_percent: number | null;
  telemetry_age_seconds: number | null;
  /**
   * Which gate produced this verdict, and the one that will judge the aircraft at launch: the
   * DOCK gate for an airframe sitting in its bay, the AIRCRAFT verdict for anything else. The
   * endpoint used to return the aircraft verdict for everything, which was wrong in both
   * directions for a dock fleet — a parked, powered-down aircraft read NO-GO on an unreported
   * battery and a stale link that the dock gate does not check, while an offline dock or one
   * over the wind limit read GO because nothing in that verdict can see a dock at all.
   */
  gate: 'aircraft' | 'dock';
  /** The dock this aircraft belongs to, when it has one. */
  dock_id: string | null;
  /**
   * Per-check detail. Present ONLY for `gate: 'aircraft'` — the dock gate reports a flat list of
   * failed checks and has no equivalent breakdown. `reasons` is the field to render; this is
   * supplementary.
   */
  checks?: {
    grounded: ReadinessCheck;
    battery: ReadinessCheck;
    telemetry_link: ReadinessCheck;
    geofence: ReadinessCheck;
  };
}

/** The `data` envelope of `GET /api/drones/readiness`. Pre-ranked GO-first by the server. */
export interface FleetReadiness {
  evaluated_at: string;
  min_battery_percent: number;
  stale_after_seconds: number;
  route_waypoint_count: number;
  keep_in_fence_count: number;
  drones: DroneReadiness[];
}

/**
 * The route as `GET /api/drones/readiness?waypoints=` wants it: `lat,lng` pairs joined by `;`.
 *
 * Six decimals (~0.1 m) rather than the raw float: the server matches each number against
 * `^-?\d{1,3}(\.\d{1,10})?$` and rejects the whole request on the first that does not fit, so a
 * coordinate that has picked up float noise past ten decimals would 400 the entire rail.
 *
 * Null for an empty route — the parameter is optional, and sending an empty string is a 400
 * (`z.string().min(1)`), not "no route".
 */
export function readinessRouteParam(waypoints: DraftWaypoint[]): string | null {
  if (waypoints.length === 0) return null;
  return waypoints.map((wp) => `${wp.lat.toFixed(6)},${wp.lng.toFixed(6)}`).join(';');
}

/** GO, NO-GO, or "we do not have a verdict for this aircraft" — never silently GO. */
export type RailVerdict = 'go' | 'no_go' | 'unknown';

/** One row of the rail, already formatted-agnostic but fully resolved. */
export interface RailRow {
  droneId: string;
  /** UPPERCASE, aviation style. Falls back to the registry name when there is no callsign. */
  callsign: string;
  /** Model, or the manufacturer, or the registry name — whatever identifies the airframe. */
  model: string;
  verdict: RailVerdict;
  /** Why it is NO-GO, straight from the server. Empty for GO and for unknown. */
  reasons: string[];
  batteryPercent: number | null;
  telemetryAgeSeconds: number | null;
}

function railCallsign(callsign: string | null, name: string): string {
  const label = callsign?.trim() || name.trim();
  return (label || 'UNNAMED').toUpperCase();
}

/**
 * Merge the server's ranked verdict onto the roster the canvas already holds.
 *
 * The roster is what makes a failed readiness fetch survivable: with `readiness === null` every
 * drone is still listed and still selectable, marked `unknown` rather than GO. Claiming readiness
 * we did not receive is the one thing this must never do.
 *
 * When a verdict IS present its order is kept as-is — the server ranks GO-first, then battery,
 * then link age, and re-sorting here would be a second, quietly different opinion. Roster drones
 * the verdict does not mention (registered between the two reads) are appended as `unknown` so an
 * aircraft can never vanish from the picker.
 */
export function buildRailRows(
  drones: Pick<Drone, 'id' | 'name' | 'callsign' | 'model' | 'manufacturer'>[],
  readiness: DroneReadiness[] | null,
): RailRow[] {
  const byId = new Map(drones.map((d) => [d.id, d]));
  const describe = (id: string, fallbackName: string): string => {
    const drone = byId.get(id);
    return drone?.model?.trim() || drone?.manufacturer?.trim() || fallbackName.trim() || '—';
  };

  if (readiness === null) {
    return drones.map((d) => ({
      droneId: d.id,
      callsign: railCallsign(d.callsign, d.name),
      model: describe(d.id, d.name),
      verdict: 'unknown' as const,
      reasons: [],
      batteryPercent: null,
      telemetryAgeSeconds: null,
    }));
  }

  const seen = new Set<string>();
  const rows: RailRow[] = readiness.map((r) => {
    seen.add(r.drone_id);
    return {
      droneId: r.drone_id,
      callsign: railCallsign(r.callsign, r.name),
      model: describe(r.drone_id, r.name),
      verdict: r.ready ? ('go' as const) : ('no_go' as const),
      reasons: r.reasons,
      batteryPercent: r.battery_percent,
      telemetryAgeSeconds: r.telemetry_age_seconds,
    };
  });

  for (const d of drones) {
    if (seen.has(d.id)) continue;
    rows.push({
      droneId: d.id,
      callsign: railCallsign(d.callsign, d.name),
      model: describe(d.id, d.name),
      verdict: 'unknown',
      reasons: [],
      batteryPercent: null,
      telemetryAgeSeconds: null,
    });
  }
  return rows;
}

/**
 * Link age, terse and monospace-friendly: `4s`, `12m`, `3h`, `--` when it has never reported.
 * Same bucketing as `formatAge` in `src/services/aircraft-readiness.ts`, so the row and the NO-GO
 * reason underneath it cannot print two different ages for the same aircraft.
 */
export function formatLinkAge(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '--';
  const whole = Math.max(0, Math.round(seconds));
  if (whole < 120) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  if (minutes < 120) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
}

/**
 * When the verdict on screen was computed, as `0912Z`. Junk (or a missing stamp) prints `----Z`
 * rather than an Invalid Date — the whole point of the stamp is that the operator can trust it.
 */
export function formatEvaluatedAt(iso: string | null | undefined): string {
  if (!iso) return '----Z';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '----Z';
  return utcStamp(new Date(ms));
}

/** Charge, or `--`. Never `0%` for an aircraft that simply did not report one. */
export function formatBattery(percent: number | null): string {
  if (percent === null || !Number.isFinite(percent)) return '--';
  return `${Math.round(percent)}%`;
}

export type StatusTone = 'green' | 'amber' | 'red' | 'gray';

/**
 * Battery colour, anchored to the server's own launch minimum rather than a second hardcoded
 * number: below it is a refusal (red), within one band above it is thin (amber), the rest is
 * green. An unreported battery is gray — grey is "unknown", and colouring it red would invent a
 * reading we do not have.
 */
export function batteryTone(percent: number | null, minLaunchPercent: number): StatusTone {
  if (percent === null || !Number.isFinite(percent)) return 'gray';
  if (percent < minLaunchPercent) return 'red';
  if (percent < minLaunchPercent * 2) return 'amber';
  return 'green';
}

/** Bar fill, clamped to 0–100 so a bogus reading cannot overflow the track. */
export function batteryBarPercent(percent: number | null): number {
  if (percent === null || !Number.isFinite(percent)) return 0;
  return Math.min(100, Math.max(0, Math.round(percent)));
}

export const RAIL_VERDICT_LABEL: Record<RailVerdict, string> = {
  go: 'GO',
  no_go: 'NO-GO',
  unknown: 'UNKNOWN',
};

/**
 * The accessible name of one rail row.
 *
 * Composed rather than left to the row's own text: read verbatim, the row is a pile of
 * abbreviations (`82%`, `4s`) with no clue what they measure, and the verdict — the single most
 * important word — would arrive last. This front-loads the aircraft and the verdict and spells
 * the telemetry out.
 */
export function railRowAccessibleName(row: RailRow): string {
  const parts = [
    `${row.callsign}, ${row.model}`,
    row.verdict === 'go'
      ? 'GO'
      : row.verdict === 'no_go'
        ? 'NO-GO'
        : 'readiness unknown',
    row.batteryPercent === null
      ? 'battery not reported'
      : `battery ${Math.round(row.batteryPercent)} percent`,
    row.telemetryAgeSeconds === null
      ? 'no telemetry link'
      : `link ${formatLinkAge(row.telemetryAgeSeconds)} old`,
  ];
  if (row.reasons.length > 0) parts.push(row.reasons.join('. '));
  return `${parts.join('. ')}.`;
}

/**
 * Total length of the drawn route in metres. 0 for fewer than two points.
 *
 * Measured with the SAME great-circle function the rest of the planning code uses, rather than a
 * local copy of it. The copy this replaces carried its own `EARTH_RADIUS_M`, which meant the
 * distance printed beside a route and the ETA derived from it (`estimateFlightTimeS`, which goes
 * through `haversineDistanceM`) came from two implementations that nothing kept in step.
 */
export function draftRouteDistanceM(waypoints: DraftWaypoint[]): number {
  let total = 0;
  for (let i = 1; i < waypoints.length; i += 1) {
    total += haversineDistanceM(waypoints[i - 1], waypoints[i]);
  }
  return total;
}
